import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Icon from 'components/AppIcon';
import FooterSection from '../landing-page/components/FooterSection';
import GoBackButton from '../../components/GoBackButton';
import { useLanguage } from '../../contexts/LanguageContext';

const faqData = [
  {
    category: 'Property Analysis',
    icon: 'Brain',
    color: '#00D4FF',
    questions: [
      {
        q: 'How accurate is the AI property analysis?',
        a: 'Our AI analysis achieves 85–92% accuracy on fair market value estimates, validated against actual sale prices. Each analysis includes a confidence score (0–100) reflecting data quality and market comparability. Higher scores indicate more comparable sales data available in the area.'
      },
      {
        q: 'Which property portals does ListingLens support?',
        a: 'ListingLens supports all major Australian portals including Domain, realestate.com.au, and state-specific portals. For UAE, we support Property Finder, Bayut, and Dubizzle. You can paste any listing URL or enter property details manually.'
      },
      {
        q: 'How long does a property analysis take?',
        a: 'Most analyses complete within 15–45 seconds. Complex properties or those in areas with limited comparable sales may take up to 2 minutes. You\'ll see a real-time progress indicator during analysis.'
      },
      {
        q: 'What does the confidence score mean?',
        a: 'The confidence score (0–100) reflects how reliable the analysis is based on: number of comparable sales in the area (40%), recency of comparable data (30%), property uniqueness (20%), and data completeness (10%). Scores above 75 are considered high confidence.'
      },
      {
        q: 'Can I analyse off-plan properties?',
        a: 'Yes! Our Off-Plan Analytics & ROI tool is specifically designed for off-plan properties in the UAE. It analyses developer track records, payment plan structures, projected ROI, and RERA compliance status.'
      }
    ]
  },
  {
    category: 'Listings',
    icon: 'Home',
    color: '#7B2FFF',
    questions: [
      {
        q: 'How do I create a property listing?',
        a: 'Click "List Your Property" from the navigation or dashboard. Our step-by-step wizard guides you through: property basics (address, type, features), photo upload with AI enhancement, AI-generated listing copy, pricing guidance, and review & publish. The entire process takes about 10–15 minutes.'
      },
      {
        q: 'What are the photo requirements for listings?',
        a: 'We recommend minimum 8 photos, at least 1200×800px resolution. Our AI photo enhancement tool can improve lighting, remove clutter, and optimise images for web display. Accepted formats: JPG, PNG, WEBP. Maximum 20MB per photo.'
      },
      {
        q: 'How does AI pricing guidance work?',
        a: 'Our pricing tool analyses recent comparable sales within 1km (adjustable), property features, market trends, and seasonal factors to suggest an optimal listing price range. It also shows how your price compares to similar properties currently on market.'
      },
      {
        q: 'How long until my listing goes live?',
        a: 'Listings are reviewed and published within 2–4 hours during business hours (9am–6pm AEST). Listings submitted outside business hours are published the next business day. You\'ll receive an email confirmation when your listing goes live.'
      }
    ]
  },
  {
    category: 'Professional Services',
    icon: 'Users',
    color: '#00D4FF',
    questions: [
      {
        q: 'How do I register as a professional?',
        a: 'Click "Join as Professional" from the navigation. Complete the multi-step registration including: category selection (agent, broker, lawyer, etc.), basic info and bio, services and pricing, portfolio upload, certifications, and coverage areas. Verification typically takes 1–3 business days.'
      },
      {
        q: 'What is the verification process for professionals?',
        a: 'We verify: professional licence numbers (real estate licence, legal practising certificate, etc.), identity documents, business registration, and professional insurance. For UAE professionals, we verify RERA registration, BRN, and ORN numbers. Verified professionals receive a badge on their profile.'
      },
      {
        q: 'How do I book a professional?',
        a: 'Browse the Professional Marketplace, filter by category and location, view profiles and reviews, then click "Book Consultation" on any professional\'s profile. Select your preferred date/time and service type. You\'ll receive a booking confirmation email.'
      },
      {
        q: 'How are payments handled for professional services?',
        a: 'Payments are made directly between you and the professional. ListingLens facilitates the connection but is not a party to payment transactions. Professionals set their own rates and payment methods. Always confirm payment terms before booking.'
      }
    ]
  },
  {
    category: 'Account & Billing',
    icon: 'CreditCard',
    color: '#7B2FFF',
    questions: [
      {
        q: 'How do I create an account?',
        a: 'Click "Sign Up" and register with your email address or use Google/Apple sign-in. You\'ll need to verify your email address. Basic accounts are free and include limited property analyses. Upgrade to a paid plan for unlimited analyses and premium features.'
      },
      {
        q: 'What subscription options are available?',
        a: 'We offer: Free (5 analyses/month, basic features), Professional ($29/month — unlimited analyses, all tools, priority support), and Agent ($79/month — everything in Professional plus listing management, bulk upload, and lead management). Annual plans save 20%.'
      },
      {
        q: 'Can I export my data?',
        a: 'Yes. Go to Account Settings → Data & Privacy → Export My Data. You can export your analysis history, saved properties, and account information in CSV or JSON format. Data exports are processed within 24 hours.'
      },
      {
        q: 'How do I delete my account?',
        a: 'Go to Account Settings → Account → Delete Account. You\'ll be asked to confirm and provide a reason. Account deletion is permanent and removes all your data within 30 days, except where retention is required by law (e.g., financial records). Contact privacy@listinglens.live for data deletion requests.'
      }
    ]
  },
  {
    category: 'Security & Privacy',
    icon: 'Shield',
    color: '#00D4FF',
    questions: [
      {
        q: 'How is my data protected?',
        a: 'We use AES-256 encryption for data at rest and TLS 1.3 for data in transit. Our infrastructure is hosted on AWS with data centres in Australia (Sydney) and UAE (Dubai). We comply with Australian Privacy Act 1988 and UAE Federal Law No. 45 of 2021 (PDPL).'
      },
      {
        q: 'Is my payment information secure?',
        a: 'ListingLens does not store payment card information. All payment processing is handled by PCI-DSS compliant payment processors. We use tokenisation to reference payment methods without storing sensitive card data.'
      },
      {
        q: 'How can I control my privacy settings?',
        a: 'Go to Account Settings → Privacy to control: profile visibility, data sharing preferences, marketing communications, cookie preferences, and third-party integrations. You can opt out of non-essential data collection at any time.'
      },
      {
        q: 'What happens if there is a data breach?',
        a: 'In the event of a breach likely to cause serious harm, we will notify affected users within 72 hours and report to the relevant authority (OAIC in Australia, UAE Data Office). We maintain an incident response plan and conduct regular security audits to minimise breach risk.'
      }
    ]
  }
];

const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl overflow-hidden transition-all" style={{ border: `1px solid ${open ? 'rgba(0,212,255,0.3)' : 'rgba(255,255,255,0.06)'}`, background: open ? 'rgba(0,212,255,0.03)' : 'rgba(255,255,255,0.02)' }}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 p-5 text-left"
      >
        <span className="font-medium text-sm md:text-base" style={{ color: '#F0F4FF' }}>{question}</span>
        <div className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-all" style={{ background: open ? 'rgba(0,212,255,0.15)' : 'rgba(255,255,255,0.05)', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>
          <Icon name="ChevronDown" size={16} color={open ? '#00D4FF' : 'rgba(240,244,255,0.5)'} />
        </div>
      </button>
      {open && (
        <div className="px-5 pb-5">
          <p className="text-sm leading-relaxed" style={{ color: 'rgba(240,244,255,0.7)' }}>{answer}</p>
        </div>
      )}
    </div>
  );
};

const FAQ = () => {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const { t, language } = useLanguage();

  const categories = ['All', ...faqData?.map((c) => c?.category)];

  const filtered = useMemo(() => {
    return faqData?.filter((cat) => activeCategory === 'All' || cat?.category === activeCategory)?.map((cat) => ({
        ...cat,
        questions: cat?.questions?.filter(
          (q) =>
            !search ||
            q?.q?.toLowerCase()?.includes(search?.toLowerCase()) ||
            q?.a?.toLowerCase()?.includes(search?.toLowerCase())
        ),
      }))?.filter((cat) => cat?.questions?.length > 0);
  }, [search, activeCategory]);

  const totalResults = filtered?.reduce((sum, cat) => sum + cat?.questions?.length, 0);

  return (
    <div className="min-h-screen" style={{ background: '#050A14' }}>
      {/* Nav */}
      <nav className="border-b border-border/30 px-4 md:px-8 h-16 flex items-center justify-between" style={{ background: 'rgba(5,10,20,0.95)', backdropFilter: 'blur(12px)' }}>
        <Link to="/" className="flex items-center gap-3">
          <img
            src="/assets/images/logo_LL-1772022913346.jpeg"
            alt="ListingLens logo"
            className="h-9 w-auto rounded-lg object-cover"
            style={{ boxShadow: '0 0 12px rgba(0,212,255,0.4)', border: '1px solid rgba(0,212,255,0.3)', maxWidth: '130px' }}
          />
        </Link>
        <div className="flex items-center gap-4">
          <GoBackButton fallbackPath="/" label={t('goBack')} />
          <Link to="/help-support" className="text-sm text-muted-foreground hover:text-primary transition-colors">{t('helpCenter')}</Link>
          <Link to="/contact-us" className="text-sm px-4 py-2 rounded-lg font-medium transition-colors" style={{ background: 'rgba(0,212,255,0.1)', color: '#00D4FF', border: '1px solid rgba(0,212,255,0.3)' }}>{t('contactUs')}</Link>
        </div>
      </nav>
      {/* Hero */}
      <section className="py-16 md:py-24 px-4 md:px-8 text-center">
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6" style={{ background: 'rgba(0,212,255,0.1)', color: '#00D4FF', border: '1px solid rgba(0,212,255,0.2)' }}>
            <Icon name="HelpCircle" size={14} />
            {t('faqTitle')}
          </div>
          <h1 className="text-4xl md:text-5xl font-black mb-4" style={{ color: '#F0F4FF' }}>{t('howCanWeHelp')}</h1>
          <p className="text-lg mb-8" style={{ color: 'rgba(240,244,255,0.6)' }}>{t('faqSubtitle')}.</p>

          {/* Search */}
          <div className="relative max-w-xl mx-auto">
            <div className="absolute left-4 top-1/2 -translate-y-1/2">
              <Icon name="Search" size={18} color="rgba(240,244,255,0.4)" />
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e?.target?.value)}
              placeholder={t('searchFAQ')}
              className="w-full pl-12 pr-4 py-4 rounded-xl text-base outline-none transition-all"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#F0F4FF' }}
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-4 top-1/2 -translate-y-1/2">
                <Icon name="X" size={16} color="rgba(240,244,255,0.4)" />
              </button>
            )}
          </div>
          {search && (
            <p className="mt-3 text-sm" style={{ color: 'rgba(240,244,255,0.5)' }}>
              {totalResults} result{totalResults !== 1 ? 's' : ''} for "{search}"
            </p>
          )}
        </div>
      </section>
      <div className="max-w-5xl mx-auto px-4 md:px-8 pb-20">
        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-10 justify-center">
          {categories?.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className="px-4 py-2 rounded-full text-sm font-medium transition-all"
              style={{
                background: activeCategory === cat ? 'rgba(0,212,255,0.15)' : 'rgba(255,255,255,0.04)',
                color: activeCategory === cat ? '#00D4FF' : 'rgba(240,244,255,0.6)',
                border: `1px solid ${activeCategory === cat ? 'rgba(0,212,255,0.4)' : 'rgba(255,255,255,0.08)'}`,
              }}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* FAQ Content */}
        {filtered?.length === 0 ? (
          <div className="text-center py-16">
            <Icon name="SearchX" size={40} color="rgba(240,244,255,0.2)" />
            <p className="mt-4 text-lg font-medium" style={{ color: 'rgba(240,244,255,0.5)' }}>{t('noQuestionsFound')}</p>
            <p className="text-sm mt-2" style={{ color: 'rgba(240,244,255,0.3)' }}>{t('tryDifferentSearch')}</p>
            <button onClick={() => { setSearch(''); setActiveCategory('All'); }} className="mt-4 text-sm" style={{ color: '#00D4FF' }}>{t('clearFiltersBtn')}</button>
          </div>
        ) : (
          <div className="space-y-10">
            {filtered?.map((cat) => (
              <div key={cat?.category}>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${cat?.color}15` }}>
                    <Icon name={cat?.icon} size={18} color={cat?.color} />
                  </div>
                  <h2 className="text-lg font-bold" style={{ color: '#F0F4FF' }}>{cat?.category}</h2>
                  <span className="text-xs px-2 py-1 rounded-full" style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(240,244,255,0.4)' }}>{cat?.questions?.length}</span>
                </div>
                <div className="space-y-3">
                  {cat?.questions?.map((item) => (
                    <FAQItem key={item?.q} question={item?.q} answer={item?.a} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Still need help */}
        <div className="mt-16 p-8 rounded-2xl text-center" style={{ background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.15)' }}>
          <Icon name="MessageCircle" size={32} color="#00D4FF" />
          <h3 className="text-xl font-bold mt-4 mb-2" style={{ color: '#F0F4FF' }}>{t('stillHaveQuestions')}</h3>
          <p className="mb-6" style={{ color: 'rgba(240,244,255,0.6)' }}>{t('cantFindAnswer')}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/contact-us" className="px-6 py-3 rounded-xl font-medium text-sm transition-all" style={{ background: 'linear-gradient(135deg, #00D4FF, #7B2FFF)', color: '#fff' }}>
              {t('contactUs')}
            </Link>
            <Link to="/help-support" className="px-6 py-3 rounded-xl font-medium text-sm transition-all" style={{ background: 'rgba(255,255,255,0.05)', color: '#F0F4FF', border: '1px solid rgba(255,255,255,0.1)' }}>
              {t('browseHelp')}
            </Link>
          </div>
        </div>
      </div>
      <FooterSection />
    </div>
  );
};

export default FAQ;
